import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EvaluatorPage = () => {
  const { t } = useLanguage();
  const [discipline, setDiscipline] = useState('');
  const [studentResponse, setStudentResponse] = useState('');
  const [questionContext, setQuestionContext] = useState('');
  const [evaluation, setEvaluation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEvaluate = async (e) => {
    e.preventDefault();
    if (!discipline || !studentResponse.trim()) return;

    setLoading(true);
    setEvaluation('');

    try {
      const response = await axios.post(`${API}/evaluate`, {
        discipline,
        student_response: studentResponse,
        question_context: questionContext || undefined
      });

      setEvaluation(response.data.evaluation);
    } catch (error) {
      console.error('Error evaluating response:', error);
      setEvaluation(t('error') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDiscipline('');
    setStudentResponse('');
    setQuestionContext('');
    setEvaluation('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={t('evaluator')} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Input Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-3xl mr-3">✏️</span>
              {t('student_response')}
            </h2>
            
            <form onSubmit={handleEvaluate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('discipline')}
                </label>
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="evaluator-discipline-select"
                >
                  <option value="">{t('select_discipline')}</option>
                  <option value="Physics">{t('physics')}</option>
                  <option value="Mathematics">{t('mathematics')}</option>
                  <option value="Chemistry">{t('chemistry')}</option>
                  <option value="Biology">{t('biology')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('question_context')}
                </label>
                <textarea
                  value={questionContext}
                  onChange={(e) => setQuestionContext(e.target.value)}
                  placeholder={t('enter_context')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  data-testid="question-context-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('student_response')}
                </label>
                <textarea
                  value={studentResponse}
                  onChange={(e) => setStudentResponse(e.target.value)}
                  placeholder={t('enter_response')}
                  rows={10}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  required
                  data-testid="student-response-input"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading || !discipline || !studentResponse.trim()}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold"
                  data-testid="evaluate-button"
                >
                  {loading ? t('loading') : t('evaluate')}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-semibold"
                  data-testid="reset-button"
                >
                  {t('new_chat')}
                </button>
              </div>
            </form>
          </div>

          {/* Results Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-3xl mr-3">📊</span>
              {t('evaluation_result')}
            </h2>
            
            {evaluation ? (
              <div className="prose max-w-none" data-testid="evaluation-result">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl">
                  <div className="whitespace-pre-wrap text-gray-800">{evaluation}</div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400 mt-12">
                <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg">{t('enter_response')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluatorPage;